import { ClipboardEvent, KeyboardEvent, useCallback, useRef } from 'react';
import { cn, Input } from '@centrix/shared-ui';

export const OTP_LENGTH = 4;

interface OtpInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  onComplete?: (otp: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  /** Number of digit cells. Defaults to {@link OTP_LENGTH} (4) for signup; the
   *  login MFA challenge passes 6 for a TOTP code. */
  length?: number;
}

export const OtpInput = ({
  value,
  onChange,
  onComplete,
  disabled,
  hasError,
  length = OTP_LENGTH,
}: OtpInputProps) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const setRef = useCallback(
    (index: number) => (el: HTMLInputElement | null) => {
      inputRefs.current[index] = el;
    },
    []
  );

  const focusInput = useCallback((index: number) => {
    const input = inputRefs.current[index];
    input?.focus();
    input?.select();
  }, []);

  const emitComplete = useCallback(
    (next: string[]) => {
      const code = next.join('');
      if (code.length === length && next.slice(0, length).every(Boolean)) {
        onComplete?.(code);
      }
    },
    [onComplete, length]
  );

  const handleChange = useCallback(
    (index: number, raw: string) => {
      const digit = raw.replace(/\D/g, '').slice(-1);
      if (!digit && raw.length > 0) return;

      const next = [...value];
      next[index] = digit;
      onChange(next);

      if (digit && index < length - 1) {
        focusInput(index + 1);
      }
      emitComplete(next);
    },
    [value, onChange, focusInput, emitComplete, length]
  );

  const handleKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !value[index] && index > 0) {
        focusInput(index - 1);
      }
    },
    [value, focusInput]
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
      if (!pasted) return;

      const next = [...value];
      for (let i = 0; i < pasted.length; i++) {
        next[i] = pasted[i];
      }
      onChange(next);
      focusInput(Math.min(pasted.length, length) - 1);
      emitComplete(next);
    },
    [value, onChange, focusInput, emitComplete, length]
  );

  const errorClasses = hasError
    ? 'bg-[#f2ada9]/20 border-error-dark/20 text-error-dark focus:border-error-dark focus:ring-error-dark'
    : '';

  return (
    <div className="flex gap-6 justify-center">
      {Array.from({ length }, (_, i) => (
        <Input
          key={i}
          ref={setRef(i)}
          variant="otp"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={value[i] ?? ''}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          aria-label={`Digit ${i + 1} of ${length}`}
          aria-invalid={hasError}
          className={cn(errorClasses)}
        />
      ))}
    </div>
  );
};
